"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { useTranslations } from "next-intl";

import type { SitePaywall } from "@/templates/site/types";
import { CardRedeemForm } from "@/components/site/card-redeem-form";
import { useChargeLabel, useSiteMoney } from "@/components/site/site-money";
import { ApiError, apiFetch } from "@/lib/api-client";

export default function PaywallCard({
  paywall,
  mode = "read",
}: {
  paywall: SitePaywall;
  mode?: "read" | "use";
}) {
  const t = useTranslations("site.paywall");
  const tCommon = useTranslations("site.common");
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [submitting, setSubmitting] = useState(false);
  const [cardNo, setCardNo] = useState("");
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardSuccess, setCardSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState<"pay" | "card">("pay");

  const money = useSiteMoney();
  const chargeLabel = useChargeLabel();

  const isPoints = paywall.feeType === "points";
  const priceLabel = isPoints ? t("points", { value: paywall.feeValue }) : money.base(paywall.feeValue);
  const priceHint = isPoints ? priceLabel : chargeLabel(paywall.feeValue);

  const busy = submitting || pending;

  function handleFailure(error: unknown) {
    if (error instanceof ApiError && error.status === 401) {
      const next = encodeURIComponent(window.location.pathname);
      router.push(`/member/login/?next=${next}`);
      return;
    }

    setCardError(
      error instanceof ApiError ? error.message : tCommon("networkError"),
    );
    setSubmitting(false);
  }

  async function handleUnlock() {
    if (!isPoints) {
      const query = new URLSearchParams({ kind: "content", module: paywall.module, contentId: paywall.contentId, next: window.location.pathname + window.location.search });
      router.push(`/member/checkout/purchase/?${query}`);
      return;
    }
    setCardError(null);
    setCardSuccess(null);
    setSubmitting(true);

    try {
      await apiFetch("/api/member/content/unlock", {
        method: "POST",
        body: {
          module: paywall.module,
          contentId: paywall.contentId,
        },
      });

      startTransition(() => {
        router.refresh();
      });
    } catch (error) {
      handleFailure(error);
    }
  }

  async function handleCardRedeem(e: React.FormEvent) {
    e.preventDefault();
    setCardError(null);
    setCardSuccess(null);

    const trimmed = cardNo.trim();
    if (!trimmed) {
      setCardError(t("cardRequired"));
      return;
    }

    setSubmitting(true);

    try {
      const result = await apiFetch<{ message?: string }>(
        "/api/member/cards/redeem",
        {
          method: "POST",
          body: {
            cardNo: trimmed,
            module: paywall.module,
            contentId: paywall.contentId,
          },
        },
      );

      setCardSuccess(result?.message ?? t("cardSuccess"));
      setCardNo("");

      setTimeout(() => {
        startTransition(() => {
          router.refresh();
        });
      }, 1500);
    } catch (error) {
      handleFailure(error);
    }
  }

  if (paywall.accessOnly) return <div className="mt-8 rounded-lg border p-6 text-center"><p>{paywall.requiredLevelName ? t("accessLevel", { level: paywall.requiredLevelName }) : t("accessRequired")}</p></div>;

  return (
    <div className="mt-8 rounded-2xl border border-border bg-gradient-to-b from-card to-muted/40 p-6 shadow-sm">
      <p className="text-center text-base font-medium text-foreground">
        {mode === "use" ? t("useTitle") : t("title")}
      </p>

      {/* 切换标签 */}
      <div className="mt-4 flex rounded-lg bg-muted p-1">
        <button
          type="button"
          onClick={() => setTab("pay")}
          className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
            tab === "pay"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {mode === "use" ? t("useTabPay") : t("tabPay")}
        </button>
        <button
          type="button"
          onClick={() => setTab("card")}
          className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
            tab === "card"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("tabCard")}
        </button>
      </div>

      {/* 支付解锁 */}
      {tab === "pay" && (
        <div className="mt-4">
          <p className="text-center text-sm text-muted-foreground">
            {t.rich(mode === "use" ? "usePayHint" : "payHint", {
              price: () => (
                <span className="font-semibold text-brand">{priceHint}</span>
              ),
            })}
          </p>
          <button
            type="button"
            onClick={handleUnlock}
            disabled={busy}
            className="mt-4 w-full rounded-full bg-primary px-8 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            {/* 原先这里是没加 ${} 的模板串，按钮上会直接显示「{priceLabel}」四个字 */}
            {busy
              ? t("processing")
              : t(mode === "use" ? "useButton" : "unlock", { price: priceLabel })}
          </button>
        </div>
      )}

      {/* 激活卡解锁 */}
      {tab === "card" && (
        <div className="mt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
            <CardRedeemForm
              title={t("cardTitle")}
              cardNo={cardNo}
              onCardNoChange={setCardNo}
              onSubmit={handleCardRedeem}
              busy={busy}
              error={cardError}
              success={cardSuccess}
            />
          </div>
        </div>
      )}
    </div>
  );
}
